/**
 * GuestBanner — persistent strip for anonymous "explore without signing up"
 * users. Rendered in DashboardLayout in the same band as SyncBanner (directly
 * below the app header) so it shows on every page until the workspace is
 * claimed. Mirrors SyncBanner's styling/tokens for visual consistency.
 *
 * Unclaimed guest workspaces lapse when the 7-day JWT expires, so the strip
 * counts down the days remaining and nudges the user to verify their email.
 */
import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { isAnonymousUser } from '../utils/anonymous';
import { getToken, parseJwt } from '../utils/auth';
import { useClaimGate } from '../pages/onboarding/claim/ClaimGateContext';

interface GuestBannerProps {
  ml?: number | { xs?: number; md?: number };
  mr?: number | { xs?: number; md?: number };
}

/** Whole days remaining until the current JWT expires, or null if unknown. */
function daysUntilTokenExpiry(): number | null {
  const token = getToken();
  if (!token) return null;
  const payload = parseJwt(token);
  const expSec = Number(payload?.exp || 0);
  if (!expSec) return null;
  const ms = expSec * 1000 - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86400000);
}

export default function GuestBanner({ ml, mr }: GuestBannerProps) {
  const theme = useTheme();
  const { requireClaim } = useClaimGate();
  // Local bump so the banner re-evaluates (and disappears) right after a claim.
  const [, force] = React.useState(0);

  if (!isAnonymousUser()) return null;

  const tokens = theme.tokens;
  const titleColor = tokens.text.primary;
  const bodyColor = tokens.text.secondary;
  const accent = tokens.semantic.warning;
  const accentSoft = tokens.semantic.warningSoft;

  const days = daysUntilTokenExpiry();
  const expiryText =
    days === null
      ? null
      : days <= 0
        ? 'Your guest workspace expires today'
        : `Your guest workspace auto-deletes in ${days} day${days === 1 ? '' : 's'}`;

  const onVerify = () => requireClaim('save_workspace', () => force((n) => n + 1));

  const lockDot = (
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
      <LockOutlined sx={{ fontSize: 16, color: accent }} />
    </Box>
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: ml ? (typeof ml === 'number' ? `${ml}px` : { xs: `${ml.xs ?? 0}px`, md: `${ml.md ?? 0}px` }) : undefined,
        mr: mr ? (typeof mr === 'number' ? `${mr}px` : { xs: `${mr.xs ?? 0}px`, md: `${mr.md ?? 0}px` }) : undefined,
        bgcolor: accentSoft,
        borderBottom: `1px solid ${tokens.border.subtle}`,
      }}
    >
      {/* Mobile: stacked */}
      <Box sx={{ display: { xs: 'block', sm: 'none' }, px: { xs: 1.5, sm: 2 }, py: 1.25 }}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {lockDot}
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: titleColor }}>
              You&apos;re exploring as a guest
            </Typography>
          </Stack>
          {expiryText && (
            <Typography sx={{ fontSize: '0.78125rem', color: bodyColor, lineHeight: 1.4 }}>
              {expiryText}. Verify your email to keep it.
            </Typography>
          )}
          <Button
            fullWidth
            variant="outlined"
            size="medium"
            onClick={onVerify}
            sx={{
              fontWeight: 600, fontSize: '0.8125rem', textTransform: 'none',
              color: accent, borderColor: tokens.border.default, py: 1,
              '&:hover': { borderColor: accent, bgcolor: accentSoft },
            }}
          >
            Verify email
          </Button>
        </Stack>
      </Box>

      {/* sm+: single horizontal row */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          px: { sm: 2, md: 3 }, py: 0.75,
          alignItems: 'center', gap: 1.5, minHeight: 40,
        }}
      >
        {lockDot}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8125rem', color: titleColor, whiteSpace: 'nowrap' }}>
            You&apos;re exploring as a guest
          </Typography>
          {expiryText && (
            <>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: bodyColor, whiteSpace: 'nowrap' }}>·</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: bodyColor, whiteSpace: 'nowrap' }}>
                {expiryText}
              </Typography>
            </>
          )}
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: bodyColor, whiteSpace: 'nowrap' }}>·</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: bodyColor, whiteSpace: 'nowrap' }}>
            Verify your email to keep it
          </Typography>
        </Stack>
        <Button
          size="small"
          startIcon={<LockOutlined sx={{ fontSize: '14px !important' }} />}
          onClick={onVerify}
          sx={{
            fontWeight: 600, fontSize: '0.8125rem', textTransform: 'none',
            color: accent, borderRadius: `${theme.ds.radius.sm}px`, px: 1, flexShrink: 0,
            '&:hover': { bgcolor: accentSoft },
          }}
        >
          Verify email
        </Button>
      </Box>
    </Box>
  );
}
